import * as THREE from 'three';

/**
 * Creates an arcade machine model using Three.js primitives with simple colors
 * @param cabinetColor The main color of the arcade cabinet
 * @param screenColor The color of the screen
 * @param accentColor The color for decorative elements
 * @param gameImageUrl Optional URL to the game image to display on screen
 * @returns The arcade machine group
 */
export function createArcadePrimitive(
  cabinetColor: string = '#ff3333', // Default to red like the reference
  screenColor: string = '#4444ff',
  accentColor: string = '#000000',  // Default to black for accents
  gameImageUrl: string = ''
): THREE.Group {
  // Create a group to hold all parts of the arcade machine
  const arcadeGroup = new THREE.Group();
  arcadeGroup.name = 'arcadePrimitive';
  
  // Materials
  const cabinetMaterial = new THREE.MeshStandardMaterial({ 
    color: new THREE.Color(cabinetColor),
    roughness: 0.7,
    metalness: 0.2
  });
  
  // Create screen material - use a neutral black if we'll be adding a game image
  const screenMaterial = new THREE.MeshStandardMaterial({ 
    color: gameImageUrl ? '#000000' : screenColor, 
    emissive: gameImageUrl ? '#000000' : screenColor,
    emissiveIntensity: gameImageUrl ? 0 : 0.5,
    roughness: 0.5
  });
  
  const controlsMaterial = new THREE.MeshStandardMaterial({ 
    color: '#111111',
    roughness: 0.9,
    metalness: 0.1
  });
  
  const accentMaterial = new THREE.MeshStandardMaterial({ 
    color: accentColor,
    roughness: 0.8,
    metalness: 0.2
  });
  
  const joystickMaterial = new THREE.MeshStandardMaterial({ 
    color: '#ff0000', // Red joystick
    roughness: 0.8,
    metalness: 0.4
  });
  
  // Create the main cabinet body - using separate parts instead of ExtrudeGeometry
  // Main body (bottom part)
  const mainBodyGeometry = new THREE.BoxGeometry(1.5, 1.6, 0.8);
  const mainBody = new THREE.Mesh(mainBodyGeometry, cabinetMaterial);
  mainBody.position.set(0, 0.8, 0);
  mainBody.castShadow = true;
  mainBody.receiveShadow = true;
  arcadeGroup.add(mainBody);
  
  // Middle part (slightly tapered inward)
  const middleGeometry = new THREE.BoxGeometry(1.4, 0.8, 0.7);
  const middle = new THREE.Mesh(middleGeometry, cabinetMaterial);
  middle.position.set(0, 2.0, 0);
  middle.castShadow = true;
  middle.receiveShadow = true;
  arcadeGroup.add(middle);
  
  // Top part (screen section)
  const topGeometry = new THREE.BoxGeometry(1.3, 1.0, 0.6);
  const top = new THREE.Mesh(topGeometry, cabinetMaterial);
  top.position.set(0, 2.9, 0);
  top.castShadow = true;
  top.receiveShadow = true;
  arcadeGroup.add(top);
  
  // Add black trim/accents to the cabinet edges
  const frontEdgeGeometry = new THREE.BoxGeometry(1.3, 3.4, 0.05);
  const frontEdge = new THREE.Mesh(frontEdgeGeometry, accentMaterial);
  frontEdge.position.set(0, 1.7, 0.3);
  arcadeGroup.add(frontEdge);
  
  // Create the screen section (recessed black area)
  const screenSectionGeometry = new THREE.BoxGeometry(1.2, 1.0, 0.1);
  const screenSection = new THREE.Mesh(screenSectionGeometry, accentMaterial);
  screenSection.position.set(0, 2.5, 0.35);
  arcadeGroup.add(screenSection);
  
  // Create the screen - moved forward
  const screenGeometry = new THREE.PlaneGeometry(1.1, 0.9);
  const screen = new THREE.Mesh(screenGeometry, screenMaterial);
  screen.position.set(0, 2.5, 0.45); // Moved from 0.41 to 0.45
  arcadeGroup.add(screen);
  
  // Add game image to the screen
  if (gameImageUrl) {
    addGameImageToScreen(screen, gameImageUrl);
  } else {
    // Fallback to generic graphics if no image URL provided
    addScreenGraphics(screen, screenColor);
  }
  
  // Create the control panel (angled section)
  const controlPanelGeometry = new THREE.BoxGeometry(1.3, 0.2, 0.5);
  const controlPanel = new THREE.Mesh(controlPanelGeometry, controlsMaterial);
  controlPanel.position.set(0, 1.5, 0.25);
  controlPanel.rotation.x = Math.PI / 6; // Angle the control panel
  arcadeGroup.add(controlPanel);
  
  // Add joystick
  const joystickBaseGeometry = new THREE.CylinderGeometry(0.06, 0.06, 0.02, 16);
  const joystickBase = new THREE.Mesh(joystickBaseGeometry, accentMaterial);
  joystickBase.position.set(-0.25, 1.55, 0.45);
  joystickBase.rotation.x = Math.PI / 6; // Match control panel angle
  arcadeGroup.add(joystickBase);
  
  const joystickStickGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.12, 8);
  const joystickStick = new THREE.Mesh(joystickStickGeometry, accentMaterial);
  joystickStick.position.set(-0.25, 1.62, 0.5);
  joystickStick.rotation.x = Math.PI / 6; // Match control panel angle
  arcadeGroup.add(joystickStick);
  
  const joystickBallGeometry = new THREE.SphereGeometry(0.03, 16, 16);
  const joystickBall = new THREE.Mesh(joystickBallGeometry, joystickMaterial);
  joystickBall.position.set(-0.25, 1.68, 0.55);
  arcadeGroup.add(joystickBall);
  
  // Add buttons (6 buttons in 2 rows of 3)
  const buttonGeometry = new THREE.CylinderGeometry(0.035, 0.035, 0.01, 16);
  
  // Create 6 buttons in a 2x3 grid
  const buttonPositions = [
    [0.1, 1.55, 0.45],
    [0.2, 1.55, 0.45],
    [0.3, 1.55, 0.45],
    [0.1, 1.5, 0.35],
    [0.2, 1.5, 0.35],
    [0.3, 1.5, 0.35]
  ];
  
  // Different colors for buttons
  const buttonColors = [
    '#ff0000', // Red
    '#ffff00', // Yellow
    '#00ff00', // Green
    '#0000ff', // Blue
    '#ff00ff', // Magenta
    '#ffffff'  // White
  ];
  
  buttonPositions.forEach((pos, index) => {
    const buttonMat = new THREE.MeshStandardMaterial({ 
      color: buttonColors[index],
      roughness: 0.5,
      metalness: 0.3,
      emissive: buttonColors[index],
      emissiveIntensity: 0.2
    });
    
    const button = new THREE.Mesh(buttonGeometry, buttonMat);
    button.position.set(pos[0], pos[1], pos[2]);
    button.rotation.x = Math.PI / 6; // Match control panel angle
    button.castShadow = true;
    arcadeGroup.add(button);
  });
  
  // Create the marquee (top section with game logo)
  const marqueeGeometry = new THREE.BoxGeometry(1.3, 0.4, 0.2); // Made wider to match cabinet
  const marquee = new THREE.Mesh(marqueeGeometry, cabinetMaterial);
  marquee.position.set(0, 3.6, 0.1);
  arcadeGroup.add(marquee);
  
  // Create the marquee screen/artwork
  const marqueeScreenGeometry = new THREE.PlaneGeometry(1.2, 0.3);
  const marqueeTexture = createMarqueeTexture(gameImageUrl);
  const marqueeScreenMaterial = new THREE.MeshStandardMaterial({
    map: marqueeTexture,
    emissive: new THREE.Color(0xffffff),
    emissiveIntensity: 1.0, // Increased intensity
    transparent: true,
    opacity: 1.0
  });
  const marqueeScreen = new THREE.Mesh(marqueeScreenGeometry, marqueeScreenMaterial);
  marqueeScreen.position.set(0, 3.6, 0.21);
  arcadeGroup.add(marqueeScreen);
  
  // Add coin slot
  const coinSlotGeometry = new THREE.BoxGeometry(0.15, 0.06, 0.02);
  const coinSlot = new THREE.Mesh(coinSlotGeometry, accentMaterial);
  coinSlot.position.set(-0.3, 1.0, 0.41);
  arcadeGroup.add(coinSlot);
  
  // Add coin return
  const coinReturnGeometry = new THREE.BoxGeometry(0.1, 0.05, 0.02);
  const coinReturn = new THREE.Mesh(coinReturnGeometry, accentMaterial);
  coinReturn.position.set(-0.3, 0.7, 0.41);
  arcadeGroup.add(coinReturn);
  
  // Add speaker grills
  const speakerGrillGeometry = new THREE.PlaneGeometry(0.2, 0.1);
  const speakerGrillMaterial = new THREE.MeshStandardMaterial({
    color: '#111111',
    roughness: 0.9,
    metalness: 0.1
  });
  
  // Left speaker
  const leftSpeaker = new THREE.Mesh(speakerGrillGeometry, speakerGrillMaterial);
  leftSpeaker.position.set(-0.25, 2.1, 0.41);
  arcadeGroup.add(leftSpeaker);
  
  // Right speaker
  const rightSpeaker = new THREE.Mesh(speakerGrillGeometry, speakerGrillMaterial);
  rightSpeaker.position.set(0.25, 2.1, 0.41);
  arcadeGroup.add(rightSpeaker);
  
  // Add side panels (black trim)
  const leftSidePanelGeometry = new THREE.BoxGeometry(0.05, 3.4, 0.8);
  const leftSidePanel = new THREE.Mesh(leftSidePanelGeometry, accentMaterial);
  leftSidePanel.position.set(-0.6, 1.7, 0);
  arcadeGroup.add(leftSidePanel);
  
  const rightSidePanelGeometry = new THREE.BoxGeometry(0.05, 3.4, 0.8);
  const rightSidePanel = new THREE.Mesh(rightSidePanelGeometry, accentMaterial);
  rightSidePanel.position.set(0.6, 1.7, 0);
  arcadeGroup.add(rightSidePanel);
  
  // Add kickplate at the bottom
  const kickplateGeometry = new THREE.BoxGeometry(1.2, 0.1, 0.8);
  const kickplate = new THREE.Mesh(kickplateGeometry, accentMaterial);
  kickplate.position.set(0, 0.05, 0);
  arcadeGroup.add(kickplate);
  
  // Add feet/levelers
  const feetPositions = [
    [-0.55, -0.02, -0.35],
    [0.55, -0.02, -0.35],
    [-0.55, -0.02, 0.35],
    [0.55, -0.02, 0.35]
  ];
  
  const feetGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.04, 8);
  const feetMaterial = new THREE.MeshStandardMaterial({
    color: '#000000',
    roughness: 0.9,
    metalness: 0.1
  });
  
  feetPositions.forEach(pos => {
    const foot = new THREE.Mesh(feetGeometry, feetMaterial);
    foot.position.set(pos[0], pos[1], pos[2]);
    arcadeGroup.add(foot);
  });
  
  // Add back panel
  const backPanelGeometry = new THREE.BoxGeometry(1.2, 3.4, 0.05);
  const backPanel = new THREE.Mesh(backPanelGeometry, cabinetMaterial);
  backPanel.position.set(0, 1.7, -0.425);
  arcadeGroup.add(backPanel);
  
  // Add access door on the back
  const doorGeometry = new THREE.BoxGeometry(0.8, 1.2, 0.02);
  const doorMaterial = new THREE.MeshStandardMaterial({
    color: cabinetColor,
    roughness: 0.8,
    metalness: 0.1
  });
  const door = new THREE.Mesh(doorGeometry, doorMaterial);
  door.position.set(0, 1.0, -0.41);
  arcadeGroup.add(door);
  
  // Add door handle
  const handleGeometry = new THREE.BoxGeometry(0.1, 0.05, 0.03);
  const handleMaterial = new THREE.MeshStandardMaterial({
    color: '#888888',
    roughness: 0.3,
    metalness: 0.8
  });
  const handle = new THREE.Mesh(handleGeometry, handleMaterial);
  handle.position.set(0.2, 1.0, -0.39);
  arcadeGroup.add(handle);
  
  return arcadeGroup;
}

/**
 * Adds a game image to the arcade screen
 * @param screen The screen mesh
 * @param imageUrl URL to the game image
 */
function addGameImageToScreen(screen: THREE.Mesh, imageUrl: string): void {
  const textureLoader = new THREE.TextureLoader();
  
  textureLoader.load(
    imageUrl,
    (texture) => {
      // Successfully loaded the texture
      texture.colorSpace = THREE.SRGBColorSpace;
      
      // Create a new material without any color tint to show the image as is
      const imageMaterial = new THREE.MeshStandardMaterial({
        map: texture,
        emissive: new THREE.Color(0xffffff),
        emissiveMap: texture,
        emissiveIntensity: 0.5,
        roughness: 0.5
      });
      
      // Replace the screen's material with the new one
      screen.material = imageMaterial;
    },
    undefined,
    (error) => {
      console.error('Error loading game image:', error);
      // Fallback to generic graphics if image fails to load
      addScreenGraphics(screen, '#' + (screen.material as THREE.MeshStandardMaterial).color.getHexString());
    }
  );
}

/**
 * Creates a texture for the marquee with the game name
 */
function createMarqueeTexture(gameImageUrl: string): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 512; // Increased resolution
  canvas.height = 128; // Increased resolution
  const context = canvas.getContext('2d');
  
  if (context) {
    // Background
    context.fillStyle = '#000000'; // Black background
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Get game name from URL
    let gameName = 'ARCADE';
    if (gameImageUrl) {
      // Extract name from URL, remove extension and path
      gameName = gameImageUrl.split('/').pop()?.split('.')[0] || 'ARCADE';
      // Convert fly.pieter to FLY PIETER
      gameName = gameName.replace('.', ' ').toUpperCase();
    }
    
    // Add gradient background
    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#000066');
    gradient.addColorStop(1, '#000033');
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add the game name with glow effect
    context.shadowColor = '#ffff00';
    context.shadowBlur = 20;
    context.fillStyle = '#ffffff'; // White text
    context.font = 'bold 48px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(gameName, canvas.width / 2, canvas.height / 2);
    
    // Add a second layer of text for extra brightness
    context.shadowBlur = 0;
    context.fillStyle = '#ffffff';
    context.fillText(gameName, canvas.width / 2, canvas.height / 2);
  }
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

/**
 * Adds simple graphics to the arcade screen
 * @param screen The screen mesh
 * @param baseColor The base color of the screen
 */
function addScreenGraphics(screen: THREE.Mesh, baseColor: string): void {
  // Create a canvas texture for the screen
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 192;
  const context = canvas.getContext('2d');
  
  if (context) {
    // Fill background
    context.fillStyle = '#000000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw a simple fighting game scene
    // Platform
    context.fillStyle = '#555555';
    context.fillRect(0, 150, canvas.width, 42);
    
    // Left character (simplified Ryu)
    context.fillStyle = '#ffffff';
    // Body
    context.fillRect(50, 100, 30, 50);
    // Head
    context.fillRect(55, 80, 20, 20);
    // Arms
    context.fillRect(40, 110, 10, 10);
    context.fillRect(80, 110, 15, 5);
    // Legs
    context.fillRect(50, 150, 15, 10);
    context.fillRect(65, 150, 15, 10);
    
    // Right character (simplified opponent)
    context.fillStyle = '#ff0000';
    // Body
    context.fillRect(176, 100, 30, 50);
    // Head
    context.fillRect(181, 80, 20, 20);
    // Arms
    context.fillRect(166, 110, 10, 10);
    context.fillRect(206, 110, 10, 10);
    // Legs
    context.fillRect(176, 150, 15, 10);
    context.fillRect(191, 150, 15, 10);
    
    // Health bars
    context.fillStyle = '#ff0000';
    context.fillRect(10, 10, 100, 10);
    context.fillRect(146, 10, 100, 10);
    
    // Timer
    context.fillStyle = '#ffff00';
    context.font = 'bold 20px Arial';
    context.textAlign = 'center';
    context.fillText('99', canvas.width / 2, 20);
    
    // Names
    context.fillStyle = '#ffffff';
    context.font = '10px Arial';
    context.textAlign = 'left';
    context.fillText('RYU', 10, 30);
    context.textAlign = 'right';
    context.fillText('KEN', canvas.width - 10, 30);
  }
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  
  // Apply the texture to the screen
  (screen.material as THREE.MeshStandardMaterial).map = texture;
  (screen.material as THREE.MeshStandardMaterial).emissiveMap = texture;
}

/**
 * Creates a row of arcade machines with different colors
 * @param count Number of arcade machines to create
 * @param spacing Spacing between machines
 * @returns Group containing all arcade machines
 */
export function createArcadeRow(
  count: number = 3, 
  spacing: number = 1.8 // Spacing for machines
): THREE.Group {
  const arcadeRow = new THREE.Group();
  arcadeRow.name = 'arcadeRow';
  
  // Define some color schemes for the arcade machines
  const colorSchemes = [
    { cabinet: '#ff3333', screen: '#4444ff', accent: '#000000' }, // Red/Black (Street Fighter)
    { cabinet: '#3333ff', screen: '#44ff44', accent: '#000000' }, // Blue/Black
    { cabinet: '#33ff33', screen: '#ff44ff', accent: '#000000' }, // Green/Black
    { cabinet: '#ffff33', screen: '#44ffff', accent: '#000000' }, // Yellow/Black
    { cabinet: '#ff33ff', screen: '#ffff44', accent: '#000000' }  // Purple/Black
  ];
  
  // Define game images for some of the arcades
  const gameImages = [
    '/assets/fly.pieter.png',
    '/assets/vibe.sail.png',
    '', // Empty string for placeholder graphics
    '', // Empty string for placeholder graphics
    ''  // Empty string for placeholder graphics
  ];
  
  for (let i = 0; i < count; i++) {
    const colors = colorSchemes[i % colorSchemes.length];
    const gameImage = gameImages[i % gameImages.length];
    
    const arcade = createArcadePrimitive(colors.cabinet, colors.screen, colors.accent, gameImage);
    arcade.position.x = (i - (count - 1) / 2) * spacing;
    arcadeRow.add(arcade);
  }
  
  return arcadeRow;
}

/**
 * Creates an interactive arcade machine that can be clicked to open a URL
 * @param scene The Three.js scene to add the arcade to
 * @param position Position of the arcade machine
 * @param rotation Rotation of the arcade machine
 * @param cabinetColor Color of the arcade cabinet
 * @param screenColor Color of the screen
 * @param accentColor Color for decorative elements
 * @param url URL to open when the arcade is interacted with
 * @param label Label to display when near the arcade
 * @param gameImageUrl URL to the game image to display on screen
 * @returns The arcade machine group
 */
export function createInteractiveArcade(
  scene: THREE.Scene,
  position: THREE.Vector3,
  rotation: number = 0,
  cabinetColor: string = '#ff3333',
  screenColor: string = '#4444ff',
  accentColor: string = '#000000',
  url: string = 'https://example.com',
  label: string = 'Arcade Machine',
  gameImageUrl: string = ''
): THREE.Group {
  const arcade = createArcadePrimitive(cabinetColor, screenColor, accentColor, gameImageUrl);
  
  // Position and rotate the arcade
  arcade.position.copy(position);
  arcade.rotation.y = rotation;
  
  // Add to scene
  scene.add(arcade);
  
  // Register as interactive object if the global function exists
  if (window.registerInteractiveObject) {
    window.registerInteractiveObject(arcade, url, label);
  }
  
  return arcade;
}